import type { CampaignStatusName } from "@multi-chat/shared";

export const CAMPAIGN_DRAFT_STEPS = ["audience", "message", "review"] as const;
export type CampaignDraftStepId = (typeof CAMPAIGN_DRAFT_STEPS)[number];
export type CampaignDraftStepVisualState = "CURRENT" | "COMPLETED" | "AVAILABLE" | "LOCKED";

export function isCampaignPostLaunchStatus(status: CampaignStatusName): boolean {
  switch (status) {
    case "SCHEDULED":
    case "RUNNING":
    case "COMPLETED":
    case "FAILED":
    case "CANCELLED":
      return true;
    case "DRAFT":
      return false;
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function campaignDraftStepLocked(
  step: CampaignDraftStepId,
  input: {
    messageUnlocked: boolean;
    reviewUnlocked: boolean;
  },
): boolean {
  switch (step) {
    case "audience":
      return false;
    case "message":
      return !input.messageUnlocked;
    case "review":
      return false;
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}

/** Fraction of the scroll viewport height used as the "reading line" for the active section. */
export const CAMPAIGN_SECTION_ANCHOR_RATIO = 0.3;

/**
 * Viewed section from scroll geometry. `top` is each section's offset from the scroll viewport top,
 * so tall sections (taller than the viewport on mobile) still become active.
 */
export function campaignViewedSectionFromScroll(input: {
  sections: ReadonlyArray<{ step: CampaignDraftStepId; top: number }>;
  viewportHeight: number;
  atBottom: boolean;
  messageUnlocked: boolean;
  reviewUnlocked: boolean;
}): CampaignDraftStepId {
  const unlocked = input.sections.filter(
    (section) => !campaignDraftStepLocked(section.step, input),
  );
  if (unlocked.length === 0) return "audience";
  if (input.atBottom) return unlocked[unlocked.length - 1]!.step;
  const anchor = input.viewportHeight * CAMPAIGN_SECTION_ANCHOR_RATIO;
  let active = unlocked[0]!.step;
  for (const section of unlocked) {
    if (section.top <= anchor) active = section.step;
  }
  return active;
}

export function campaignDraftStepVisualState(
  step: CampaignDraftStepId,
  input: {
    active: CampaignDraftStepId;
    messageUnlocked: boolean;
    reviewUnlocked: boolean;
  },
): CampaignDraftStepVisualState {
  if (campaignDraftStepLocked(step, input)) {
    return "LOCKED";
  }
  if (step === input.active) {
    return "CURRENT";
  }
  switch (step) {
    case "audience":
      return input.messageUnlocked ? "COMPLETED" : "AVAILABLE";
    case "message":
      return input.reviewUnlocked ? "COMPLETED" : "AVAILABLE";
    case "review":
      return "AVAILABLE";
    default: {
      const _exhaustive: never = step;
      return _exhaustive;
    }
  }
}

export function canSelectCampaignDraftStep(
  step: CampaignDraftStepId,
  input: {
    messageUnlocked: boolean;
    reviewUnlocked: boolean;
  },
): boolean {
  return !campaignDraftStepLocked(step, input);
}

export function campaignDraftPreviousStep(
  active: CampaignDraftStepId,
): CampaignDraftStepId | null {
  switch (active) {
    case "audience":
      return null;
    case "message":
      return "audience";
    case "review":
      return "message";
    default: {
      const _exhaustive: never = active;
      return _exhaustive;
    }
  }
}

export function campaignDraftNextStep(
  active: CampaignDraftStepId,
  input: {
    messageUnlocked: boolean;
    reviewUnlocked: boolean;
  },
): CampaignDraftStepId | null {
  switch (active) {
    case "audience":
      return input.messageUnlocked ? "message" : null;
    case "message":
      return input.reviewUnlocked ? "review" : null;
    case "review":
      return null;
    default: {
      const _exhaustive: never = active;
      return _exhaustive;
    }
  }
}

export function campaignDraftNextLockedHintKey(active: CampaignDraftStepId): string | null {
  switch (active) {
    case "audience":
      return "campaigns.message.stepLocked";
    case "message":
      return "campaigns.review.stepLocked";
    case "review":
      return null;
    default: {
      const _exhaustive: never = active;
      return _exhaustive;
    }
  }
}

export function campaignShellStatusLabelKey(status: CampaignStatusName): string {
  switch (status) {
    case "DRAFT":
      return "campaigns.shell.draft";
    case "SCHEDULED":
      return "campaigns.shell.scheduled";
    case "RUNNING":
      return "campaigns.shell.processing";
    case "COMPLETED":
      return "campaigns.shell.processingFinished";
    case "FAILED":
      return "campaigns.shell.failed";
    case "CANCELLED":
      return "campaigns.shell.cancelled";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function campaignDraftConnectorFilled(
  fromStep: CampaignDraftStepId,
  input: {
    messageUnlocked: boolean;
    reviewUnlocked: boolean;
  },
): boolean {
  switch (fromStep) {
    case "audience":
      return input.messageUnlocked;
    case "message":
      return input.reviewUnlocked;
    case "review":
      return false;
    default: {
      const _exhaustive: never = fromStep;
      return _exhaustive;
    }
  }
}

export type CampaignDraftConnectorTone = "muted" | "accent";

export function campaignDraftConnectorToStep(
  fromStep: CampaignDraftStepId,
): CampaignDraftStepId | null {
  switch (fromStep) {
    case "audience":
      return "message";
    case "message":
      return "review";
    case "review":
      return null;
    default: {
      const _exhaustive: never = fromStep;
      return _exhaustive;
    }
  }
}

export function campaignDraftConnectorTone(
  fromStep: CampaignDraftStepId,
  input: {
    active: CampaignDraftStepId;
    messageUnlocked: boolean;
    reviewUnlocked: boolean;
  },
): CampaignDraftConnectorTone {
  const toStep = campaignDraftConnectorToStep(fromStep);
  if (toStep == null) {
    return "muted";
  }
  const toIndex = CAMPAIGN_DRAFT_STEPS.indexOf(toStep);
  const activeIndex = CAMPAIGN_DRAFT_STEPS.indexOf(input.active);

  if (toIndex > activeIndex) {
    return "muted";
  }
  return campaignDraftConnectorFilled(fromStep, input) ? "accent" : "muted";
}
